import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createAgent, updateAgent, getAgentById } from '../api';
import { fetchBranches } from '../../branches/api';
import {
  Form, Input, InputNumber, Select, Button, Card, Typography,
  Row, Col, Spin, message, Space,
} from 'antd';
import { SaveOutlined, ArrowLeftOutlined } from '@ant-design/icons';

const { Title } = Typography;

export default function AgentForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    fetchBranches({}).then((res) => setBranches(res.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    getAgentById(id)
      .then((res) => {
        const agent = res.data;
        form.setFieldsValue({
          name: agent.name,
          phone: agent.phone,
          email: agent.email,
          address: agent.address,
          defaultCommissionPercent: agent.defaultCommissionPercent,
          status: agent.status,
          branch: agent.branch?._id || agent.branch,
        });
      })
      .catch(() => message.error('Failed to load agent'))
      .finally(() => setLoading(false));
  }, [id, isEdit, form]);

  const onFinish = async (values) => {
    setSubmitting(true);
    try {
      if (isEdit) {
        await updateAgent(id, values);
        message.success('Agent updated');
      } else {
        await createAgent(values);
        message.success('Agent created');
      }
      navigate('/agents');
    } catch (err) {
      message.error(err.response?.data?.message || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={4} style={{ margin: 0 }}>
            {isEdit ? 'Edit Agent' : 'Create Agent'}
          </Title>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/agents')}>
            Back
          </Button>
        </div>

        <Form form={form} layout="vertical" onFinish={onFinish} initialValues={{ defaultCommissionPercent: 0, status: 'ACTIVE' }}>
          <Row gutter={16}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="name" label="Agent Name" rules={[{ required: true, message: 'Name is required' }]}>
                <Input placeholder="Enter agent name" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="phone" label="Phone" rules={[{ required: true, message: 'Phone is required' }]}>
                <Input placeholder="Enter phone number" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="email" label="Email">
                <Input placeholder="Enter email (optional)" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="address" label="Address">
                <Input.TextArea rows={2} placeholder="Enter address" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="defaultCommissionPercent"
                label="Default Commission %"
                rules={[{ required: true, message: 'Commission % is required' }]}
              >
                <InputNumber min={0} max={100} step={0.1} style={{ width: '100%' }} placeholder="e.g. 2.5" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="branch" label="Branch">
                <Select placeholder="Select branch" allowClear showSearch optionFilterProp="children">
                  {branches.map((b) => (
                    <Select.Option key={b._id} value={b._id}>
                      {b.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {isEdit && (
            <Row gutter={16}>
              <Col xs={24} sm={12} md={8}>
                <Form.Item name="status" label="Status">
                  <Select>
                    <Select.Option value="ACTIVE">Active</Select.Option>
                    <Select.Option value="INACTIVE">Inactive</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          )}

          <Space>
            <Button type="primary" htmlType="submit" loading={submitting} icon={<SaveOutlined />}>
              {isEdit ? 'Update Agent' : 'Create Agent'}
            </Button>
            <Button onClick={() => navigate('/agents')}>Cancel</Button>
          </Space>
        </Form>
      </Card>
    </div>
  );
}
